import React from 'react';
import { Chip, Tooltip, useTheme, SxProps, Theme } from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';

interface AIBadgeProps {
  size?: 'small' | 'medium';
  tooltip?: string;
  variant?: 'filled' | 'outlined'; // Updated to use correct MUI Chip variants
  sx?: SxProps<Theme>;
}

/**
 * AIBadge - A component that indicates AI-powered features
 * 
 * @param size - The size of the badge ('small' or 'medium')
 * @param tooltip - Custom tooltip text (defaults to "AI-powered")
 * @param variant - The style variant ('filled' or 'outlined')
 * @param sx - Additional styles using the MUI sx prop
 */
export const AIBadge: React.FC<AIBadgeProps> = ({ 
  size = 'small', 
  tooltip = "AI-powered feature", 
  variant = 'filled', // Default to filled instead of default
  sx = {}
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  return (
    <Tooltip title={tooltip} arrow>
      <Chip
        icon={<SmartToyOutlinedIcon fontSize={size === 'small' ? 'small' : 'medium'} />}
        label="AI"
        size={size}
        variant={variant}
        sx={{
          height: size === 'small' ? '20px' : '24px',
          fontSize: size === 'small' ? '0.7rem' : '0.8rem',
          fontWeight: 500,
          backgroundColor: variant === 'filled' 
            ? isDarkMode 
              ? 'rgba(155, 107, 254, 0.18)' // More visible purple background in dark mode
              : 'rgba(75, 0, 130, 0.1)'      // Keep light mode as is
            : 'transparent',
          borderColor: isDarkMode ? 'rgba(155, 107, 254, 0.5)' : 'rgba(75, 0, 130, 0.3)',
          color: isDarkMode ? '#bf9fff' : 'rgb(75, 0, 130)',
          mr: 0.5,
          '& .MuiChip-icon': {
            color: isDarkMode ? '#bf9fff' : 'rgb(75, 0, 130)',
          },
          // Merge the user-provided sx props with our default styles
          ...sx
        }}
      />
    </Tooltip>
  );
};

export default AIBadge;